import React, { useCallback, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  Tooltip,
  IconButton,
  useClipboard,
} from '@chakra-ui/react';
import { QRCodeCanvas } from 'qrcode.react';
import { FiDownload, FiCopy, FiCheck } from 'react-icons/fi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  /** File-name prefix for the downloaded PNG; defaults to "qr". */
  filenameBase?: string;
}

/** Small popup that renders a QR for a short URL with a download-as-PNG action. */
export const QrCodeModal: React.FC<Props> = ({ isOpen, onClose, url, filenameBase = 'qr' }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { onCopy, hasCopied } = useClipboard(url);

  const handleDownload = useCallback(() => {
    const canvas = ref.current?.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${filenameBase}.png`;
    a.click();
  }, [filenameBase]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>QR code</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box
              ref={ref}
              bg="white"
              p={4}
              borderWidth="1px"
              rounded="md"
              display="flex"
              justifyContent="center"
            >
              <QRCodeCanvas value={url} size={208} level="M" includeMargin />
            </Box>
            <HStack
              bg="gray.50"
              borderWidth="1px"
              rounded="md"
              px={3}
              py={2}
              spacing={2}
              align="center"
            >
              <Text fontFamily="mono" fontSize="sm" color="var(--brand-primary)" isTruncated flex={1}>
                {url}
              </Text>
              <Tooltip label={hasCopied ? 'Copied!' : 'Copy URL'}>
                <IconButton
                  aria-label="Copy short URL"
                  size="xs"
                  variant="ghost"
                  icon={<Icon as={hasCopied ? FiCheck : FiCopy} />}
                  colorScheme={hasCopied ? 'green' : 'gray'}
                  onClick={onCopy}
                />
              </Tooltip>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button leftIcon={<Icon as={FiDownload} />} onClick={handleDownload}>
            Download PNG
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
